import React from 'react';
import _ from 'lodash';
import { Modal, Button, Result } from 'antd';
import { ReportErrorModalForm, FormValues } from './ReportErrorModalForm';
import useTranslation from 'next-translate/useTranslation';
import { AdminAuthContext, UserAuthContext } from '@context';
import { handleError } from '@utils/error';
import {
  SendErrorReportUser,
  UserType,
  useSendErrorReportMutation,
} from '@graphql/user/generated/graphql.react';
import { ErrorBoundary } from '../ErrorBoundary';
import { generateHumanCode } from '@utils/data';
import { apolloUserClientSignal } from '@/globals';

type Props = {};

export const ReportErrorModal: React.FC<Props> = () => {
  const [userClient] = apolloUserClientSignal.use();
  if (!userClient) throw new Error('User API not ready');

  const { t } = useTranslation();
  const userContext = React.useContext(UserAuthContext);
  const adminContext = React.useContext(AdminAuthContext);
  const [reportSent, setReportSent] = React.useState(false);
  const [sendErrorReport, sendErorrReportStatus] = useSendErrorReportMutation({
    client: userClient,
  });
  const [modalOpen, setModalOpen] = React.useState(false);

  const handleCloseModal = React.useCallback(() => {
    setReportSent(false);
    setModalOpen(false);
  }, []);

  const handleReportProblemClick = React.useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleSubmit = React.useCallback(
    async (values: FormValues) => {
      try {
        let user: SendErrorReportUser | null = null;
        if (
          adminContext.authInfo &&
          adminContext.authInfo.userInfo?.email &&
          adminContext.authInfo.tenantInfo
        ) {
          user = {
            userType: UserType.Admin,
            tenant: adminContext.authInfo.tenantInfo.tenant,
            userId: adminContext.authInfo.userInfo.sub,
            email: adminContext.authInfo.userInfo.email,
          };
        } else if (userContext.authInfo && userContext.authInfo.authState) {
          user = {
            userType: UserType.User,
            tenant: userContext.authInfo.authState.organization.tenant,
            userId: userContext.authInfo.authState.participant._id,
            email: userContext.authInfo.authState.participant.email,
          };
        }

        const reportId = generateHumanCode(12);
        ErrorBoundary.bugsnagClient?.notify({
          name: reportId,
          message: values.title,
        });
        const result = await sendErrorReport({
          variables: {
            input: {
              user,
              reportId,
              title: values.title,
              message: values.message,
              files: _.compact(
                values.files?.map((file) => file.response?.url) ?? [],
              ),
            },
          },
        });
        if (result.data?.sendErrorReport.success) {
          setReportSent(true);
        }
      } catch (err) {
        handleError(err, { t });
      }
    },
    [t, userContext, adminContext, sendErrorReport],
  );

  return (
    <div className="flex justify-end items-center m-6">
      <Button type="default" onClick={handleReportProblemClick}>
        {t('global:report-error-modal.trigger')}
      </Button>
      <Modal
        destroyOnHidden={true}
        open={modalOpen}
        okText={t('global:send')}
        confirmLoading={sendErorrReportStatus.loading}
        okButtonProps={{
          form: 'report-error',
          htmlType: 'submit',
          disabled: reportSent,
        }}
        cancelButtonProps={{
          disabled: reportSent,
        }}
        cancelText={t('global:cancel')}
        title={t('global:report-error-modal.title')}
        onCancel={handleCloseModal}
        footer={reportSent ? null : undefined}
      >
        {reportSent ? (
          <Result status="success" />
        ) : (
          <ReportErrorModalForm onSubmit={handleSubmit} />
        )}
      </Modal>
    </div>
  );
};
